// @flow
import React, { useEffect } from 'react';
import { compose } from 'redux';
import { useParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import { useQuery } from '@apollo/client';
import Error from '@justpark/ui/src/components/Error';
import getGraphQlErrorsByPath from '@justpark/helpers/src/getGraphQlErrorsByPath/getGraphQlErrorsByPath';
import ListingDetails from '../components/ListingDetails/ListingDetails';
import withApollo from '../decorators/withApollo';
import { gtagTrack, page } from '../helpers/Tracking/Analytics';
import GET_LISTING_DETAILS_QUERY from '../queries/listingDetails/getListingDetailsQuery.graphql';
import NotFound from '../components/Common/NotFound';
import LoadingState from '../components/ListingDetails/LoadingState';

import styles from '../../static/scss/pages/listingDetails.module.scss';

const richResultsData = (listing) => {
  const reviews = listing.reviews?.nodes || [];

  return {
    '@context': 'https://schema.org/',
    '@type': 'LocalBusiness',
    brand: {
      '@type': 'Brand',
      name: 'JustPark'
    },
    keywords:
      'parking spaces for rent, car parking, london parking, rent your driveway, let out your space, garages for rent, stadium parking, airport parking, garage rentals, parking, garages, driveways, rent, let',
    address: {
      '@type': 'PostalAddress',
      streetAddress: listing.address.line1,
      addressLocality: listing.address.city,
      postalCode: listing.address.postcode,
      addressCountry: listing.address.country
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: listing.feedback?.averageRating,
      ratingCount: listing.feedback?.count
    },
    url: listing.canonicalUrl,
    description: listing.description,
    image: listing?.photos?.[0]?.normal.url || '',
    geo: {
      '@type': 'GeoCoordinates',
      latitude: listing.address?.lat,
      longitude: listing.address?.lng
    },
    name: listing.title,
    review: reviews.map((review) => ({
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: review.rating,
        bestRating: '5'
      },
      author: {
        '@type': 'Person',
        name: review.sender
      }
    }))
  };
};

const mapSizes = [
  { width: 640, height: 270 },
  { width: 575, height: 160 },
  { width: 475, height: 160 },
  { width: 375, height: 160 },
  { width: 320, height: 160 }
];

const ListingDetailsPage = () => {
  useEffect(() => {
    page('listing-details');
    gtagTrack('listing_landing_impression');
  }, []);

  const { slug } = useParams();

  const { loading, error, data, fetchMore } = useQuery(
    GET_LISTING_DETAILS_QUERY,
    {
      variables: {
        slug,
        reviewsOffset: 0,
        mapSizes
      },
      // needed to allow loading state to be set when doing fetchMore
      notifyOnNetworkStatusChange: true
    }
  );

  const listingErrors = getGraphQlErrorsByPath(error, 'listing');
  const listingNotFound = listingErrors.find(
    (listingError) => listingError?.extensions?.status === 404
  );
  const listing = data?.listing || {};

  const is404 = listingNotFound;

  // (loading && data) needed so that we don't reload ListingDetails component when doing fetchMore
  const isOk = (loading === false && !error) || (loading && data);

  const meta = listing?.meta || [];

  return (
    <>
      <Helmet title={listing.shortListingTitle} titleTemplate="%s">
        {meta.map(({ key, value, type }) => {
          if (type === 'property') {
            return <meta key={key} property={key} content={value} />;
          }

          return <meta key={key} name={key} content={value} />;
        })}
        {isOk && <link rel="canonical" href={listing.canonicalUrl} />}

        {isOk && (
          <script type="application/ld+json">
            {JSON.stringify(richResultsData(listing))}
          </script>
        )}
      </Helmet>
      <div className={styles.wrapper}>
        {is404 && <NotFound />}
        {loading && <LoadingState />}
        {error && <Error error={error.toString()} />}
        {isOk && (
          <ListingDetails
            location={listing}
            fetchMore={fetchMore}
            loading={loading}
          />
        )}
      </div>
    </>
  );
};

export default compose(withApollo())(ListingDetailsPage);
